import {
  announceSpeechStopped,
  dropSpeechWork,
  installSpeechStopKey,
  PendingSpeechMap,
  muteQueuedSpeech,
  reportSpeechPlayback,
  resetSpeechStopKeyForTests,
  trackSpeechWork,
} from "./tts-stop-key";
import { canSpeak } from "./tts-can-speak";
/**
 * Denker ONNX text-to-speech wrapper for the desktop app.
 *
 * Lifecycle:
 *   - `speak()` queues a sidecar utterance in Tauri.
 *   - `cancel("explicit")` stops playback immediately; lifecycle cancels only
 *     drop queued speech that has not started yet.
 *   - Native playback events drive narration-state updates so the visible
 *     bubble starts revealing text when audio actually starts.
 */
import { create } from "zustand";
import { logTtsPipeline, logTtsPipelineStreamStart } from "./tts-pipeline-log";
import {
  type DenkerAgentVoiceStateEvent,
  waitForUtteranceTerminal,
  settleUtteranceWaiter,
  dropUtteranceWaiter,
  resetUtteranceWaiters,
} from "./tts-utterance-waiters";
import {
  plainSpeechText,
  normalizedSpeechText,
  remainingTextAfterPrefix,
  finalTextStartsWithPrefix,
} from "./tts-reply-text";
import {
  clearReplyHistories,
  forgetReplyHistory,
  hasRecentReplySpeak,
  markReplySpoken,
  REPLY_DEDUP_MS,
  replyTailAfterSpokenHistory,
  unspokenReplySentences,
} from "./tts-reply-history";
import { speechLookahead, prepareQueuedSpeech } from "./tts-lookahead-runtime";
import type { SpeechTicket } from "./tts-lookahead";
import {
  releasePresentationPreparation,
  takePresentationPreparation,
} from "./tts-presentation-preparation";
import { replayPresentationNarration } from "./narration-events";
import { listen, emit } from "@tauri-apps/api/event";
import { invoke } from "@tauri-apps/api/core";
import { useCanvasStore } from "@/stores/canvas-store";
import { useConversationStore } from "@/stores/conversation-store";
import { useTtsSettingsStore } from "@/stores/tts-settings-store";
import { isTauri } from "@/lib/is-tauri";
import { checkpoint as traceCheckpoint } from "@/lib/execution-timer";
import { normalizeAssistantText } from "@/lib/assistant-text-normalization";
import { getMyWindowLabel } from "@/lib/owner-state-client";
import { track } from "./observability";
import { presentationSpeechTurns } from "./tts-presentation-turns";
import { cueQuietTurn, isQuietTurn, quietForTurn } from "./tts-quiet-turns";
import { TtsPresentationQueue } from "./tts-presentation-queue";
import {
  emitInterruptedPresentationSpeech,
  isSpeechMutedForUserTurn,
  muteSpeechForUserTurn,
  resetUserSpeechInterruptionsForTests,
  unmuteSpeechForUserTurn,
} from "./tts-user-speech-interruption";
import { getWindowRoleSync } from "./use-window-role";
import { isContinuousVoiceActive } from "./continuous-voice-store";
import {
  clearLiveTalkSpeechHold,
  isLiveTalkSpeechHeld,
} from "./live-talk-speech-hold";
import {
  cancelDenkerAgentVoice,
  currentVoiceEngine,
  type DenkerAgentVoiceCancelReason,
  speakWithDenkerAgentVoice,
} from "./denker-agent-voice";
import {
  emitNarrationState,
  type NarrationStateDetail,
} from "./narration-events";
import { isAcknowledgementForExecution } from "./tts-acknowledgements";
import {
  cancelPendingTtsAcknowledgements,
  createTtsAcknowledgementActions,
  resetTtsAcknowledgementSchedulerForTests,
  setDeterministicAcknowledgementsEnabledForTests,
  supersedeTtsAcknowledgement,
  type TtsAcknowledgementActions,
} from "./tts-acknowledgement-scheduler";
import {
  type TtsSpeakingState,
  useTtsPlaybackStore,
} from "./tts-playback-store";
import {
  activeTtsEchoText,
  rememberTtsEcho,
  spokenTtsEchoTexts,
  resetTtsEchoMemoryForTests,
} from "./tts-echo-memory";
import { firstSentences, speechGroups } from "./tts-speech-segments";
import {
  resumeAnsweredLiveTalkExecution,
  isLiveTalkSpeechAllowed,
  resetLiveTalkSpeechOwnershipForTests,
} from "./live-talk-speech-ownership";

import {
  advanceStreamingReply,
  createStreamingSpeak,
  forceAdvanceStreamingReply,
  hasQueuedStreamingSpeech,
  heldTrailingSentence,
  type StreamingSpeak,
  switchStreamingMessage,
} from "./tts-streaming-reply";

export type SessionOrigin = "chat" | "heartbeat";

type State = {
  /** Forwarded sidecar lifecycle (started/finished/cancelled). Null
   *  when no utterance is in flight. */
  speakingState: TtsSpeakingState;
};

type Actions = TtsAcknowledgementActions & {
  /** Pass `null` for sentenceCount to read the full text. */
  speak: (
    text: string,
    sentenceCount?: number | null,
    options?: {
      executionId?: string;
      ttsSource?: string;
      /** Presentation speech only: drop it, in the owner window, while a
       *  sentence of the same execution is still playing or queued. */
      dropIfBusy?: boolean;
    },
  ) => void;
  speakStreamingReply: (
    executionId: string,
    cumulativeText: string,
    origin?: SessionOrigin,
    streamId?: string,
  ) => void;
  speakFinalReply: (
    executionId: string,
    text: string,
    origin?: SessionOrigin,
  ) => void;
  cancel: (reason?: DenkerAgentVoiceCancelReason) => void | false;
  /** Stop speech tagged with this source, playing or still queued - a
   *  presentation step, a question that has just been answered, or an
   *  onboarding request whose step has ended. */
  cancelPresentationSpeech: (ttsSource: string) => void;
  hasPendingPresentationSpeech: (executionId?: string) => boolean;
  /** True while an utterance is in flight (sidecar started, not yet
   *  finished/cancelled). Used by the voice-toggle hotkey to silence
   *  speech before starting dictation (barge-in). */
  isActive: () => boolean;
  speakLatestReply: (
    cursorKey: string,
    executionId?: string,
    origin?: SessionOrigin,
    agentName?: string,
    allowUnprovenancedCursorFallback?: boolean,
  ) => void;
  clearReplyDedup: (executionId: string) => void;
  /** The user answered this run's question: it speaks its next reply in
   *  full, even after a stop or a spoken turn silenced it. */
  resumeAnsweredTurn: (executionId: string) => void;
};

// Owner-side dedup: utterance text + timestamp of the most recent speak()
// invocation. Suppresses repeat speak() calls with identical text within
// SPEAK_DEDUP_MS, which happens when the active onboarding monitor changes
// and the re-activated overlay re-runs effects that emit the same bubble.
let lastSpoken: { text: string; ttsSource?: string; at: number } | null = null;
const SPEAK_DEDUP_MS = 30_000;
/**
 * A standalone utterance (bubble, guide narration, acknowledgement) already
 * read this exact text aloud. Same-execution streaming/final flow has its own
 * tail bookkeeping and is excluded via ttsSource.
 */
function wasStandaloneSpoken(text: string): boolean {
  const spoken = lastSpoken;
  if (!spoken) return false;
  const source = spoken.ttsSource ?? "";
  if (
    source !== "" &&
    source !== "acknowledgement" &&
    !source.startsWith("onboarding-bubble:") &&
    !source.startsWith("presentation:") &&
    !source.startsWith("awaiting_input:") &&
    !source.startsWith("awaiting-input:")
  ) {
    return false;
  }
  if (Date.now() - spoken.at > SPEAK_DEDUP_MS) return false;
  const prior = normalizedSpeechText(spoken.text);
  return Boolean(prior) && prior === normalizedSpeechText(text);
}
function forgetFailedSpeak(text: string, ttsSource?: string): void {
  if (lastSpoken?.text === text && lastSpoken.ttsSource === ttsSource) {
    lastSpoken = null;
  }
}
let activeNarration:
  | (Pick<NarrationStateDetail, "utteranceId" | "text" | "provider"> & {
      ttsSource?: string;
      executionId?: string;
      liveTalkOnly?: boolean;
      presentationAttemptCancelled?: boolean;
    })
  | null = null;
function isPresentationNarrating(executionId: string | undefined): boolean {
  return (
    activeNarration?.ttsSource?.startsWith("presentation:") === true &&
    activeNarration.executionId === executionId
  );
}
// Streaming waits out narration; a stopped presentation silences it.
function streamingSpeechHeld(executionId: string): boolean {
  return (
    presentationSpeechTurns.outcome(executionId) === "stopped" ||
    presentationQueue.hasPending(executionId) ||
    isPresentationNarrating(executionId)
  );
}
function rememberNarrationForEcho(
  narration: NonNullable<typeof activeNarration>,
  state: "started" | "finished" | "cancelled" | "error",
): void {
  rememberTtsEcho(narration.text, state);
}

export function getActiveTtsTextForEcho(): string | null {
  return activeTtsEchoText(activeNarration?.text);
}

/** What Denker spoke between `from` and `until` (see spokenTtsEchoTexts). */
export function getSpokenTtsTextsForEcho(
  from: number,
  until: number,
): string[] {
  return spokenTtsEchoTexts(activeNarration?.text, from, until);
}

type PendingSpeak = {
  cursorKey: string;
  executionId: string;
  origin?: SessionOrigin;
  agentName?: string;
  allowCursorFallback: boolean;
  scheduledAt: number;
};
type FinalReplyReceipt = {
  normalizedText: string;
  receivedAt: number;
  status: "pending" | "succeeded";
  delivery: "relayed" | "owned";
  duplicateWhilePending: boolean;
  text: string;
  origin?: SessionOrigin;
};
type ReplySpeechQueue = {
  token: number;
  queue: Promise<void>;
  lastSpeechSucceeded: boolean;
};
type StandaloneSpeechQueue = {
  cancellationToken: number;
  token: number;
  queue: Promise<void>;
  /** The source of the item holding `token`, so a source can be withdrawn
   *  before it starts. */
  source?: string;
};
type QueuedAcknowledgement = {
  executionId: string;
  token: number;
  liveTalkOnly: boolean;
};
const presentationQueue = new TtsPresentationQueue();
const pendingSpeaks = new PendingSpeechMap<PendingSpeak>();
const finalReplyReceipts = new Map<string, FinalReplyReceipt>();
const streamingSpeaks = new Map<string, StreamingSpeak>();
const streamingTrailingTimers = new PendingSpeechMap<
  ReturnType<typeof setTimeout>
>();
// Settle delay before speaking a sentence held only by the trailing-period
// ambiguity guard. Stream token chunks arrive within milliseconds, so a
// domain continuation ("example" -> "example.com") cancels the flush in
// time; a finished commentary sentence (tools running, no more text)
// still speaks promptly instead of staying silent.
const STREAMING_TRAILING_FLUSH_MS = 120;
const replySpeechQueues = new Map<string, ReplySpeechQueue>();
let replySpeechQueueToken = 0;
const standaloneSpeechQueue: StandaloneSpeechQueue = {
  cancellationToken: 0,
  token: 0,
  queue: Promise.resolve(),
};
let queuedAcknowledgement: QueuedAcknowledgement | null = null;
let nativeSpeechQueue: Promise<void> = Promise.resolve();
let nativeCancellation: Promise<void> = Promise.resolve();
const FINAL_MESSAGE_WAIT_MS = 350;

function preemptActiveAcknowledgement(executionId?: string): void {
  if (
    activeNarration?.ttsSource !== "acknowledgement" ||
    (executionId && activeNarration.executionId !== executionId)
  ) {
    return;
  }
  const cancelled = activeNarration;
  rememberNarrationForEcho(cancelled, "cancelled");
  activeNarration = null;
  useTTS.setState({ speakingState: "cancelled" });
  emitNarrationState({
    ...cancelled,
    state: "cancelled",
    at: Date.now(),
  });
  standaloneSpeechQueue.token += 1;
  standaloneSpeechQueue.queue = Promise.resolve();
  nativeSpeechQueue = Promise.resolve();
  void cancelDenkerAgentVoice("new_prompt");
}

function preemptActiveReplyForExecution(executionId: string): void {
  if (
    !activeNarration ||
    activeNarration.executionId !== executionId ||
    activeNarration.ttsSource === "acknowledgement"
  ) {
    return;
  }
  const cancelled = activeNarration;
  rememberNarrationForEcho(cancelled, "cancelled");
  activeNarration = null;
  useTTS.setState({ speakingState: "cancelled" });
  emitNarrationState({
    ...cancelled,
    state: "cancelled",
    at: Date.now(),
  });
  nativeSpeechQueue = cancelDenkerAgentVoice("new_prompt").catch(
    () => undefined,
  );
}

function cancelPendingAcknowledgement(executionId: string): void {
  supersedeTtsAcknowledgement(executionId);
  cancelQueuedOrActiveAcknowledgement(executionId);
}

function cancelQueuedOrActiveAcknowledgement(executionId: string): void {
  if (queuedAcknowledgement?.executionId === executionId) {
    queuedAcknowledgement = null;
  }
  preemptActiveAcknowledgement(executionId);
}

function cancelLiveTalkAcknowledgements(): void {
  if (queuedAcknowledgement?.liveTalkOnly) queuedAcknowledgement = null;
  if (activeNarration?.liveTalkOnly) preemptActiveAcknowledgement();
}

function cancelPendingAcknowledgements(): void {
  cancelPendingTtsAcknowledgements();
}

function isHardCancel(reason: DenkerAgentVoiceCancelReason): boolean {
  return (
    reason === "explicit" ||
    reason === "stop_key" ||
    reason === "voice_barge_in" ||
    reason === "user_speech"
  );
}

function acceptFinalReply(
  executionId: string,
  text: string,
  owner: boolean,
  origin?: SessionOrigin,
): boolean {
  const normalizedText = normalizedSpeechText(text);
  if (!normalizedText) return false;
  const now = Date.now();
  for (const [id, candidate] of finalReplyReceipts) {
    if (
      now - candidate.receivedAt > REPLY_DEDUP_MS &&
      (candidate.status === "succeeded" || candidate.delivery === "relayed")
    ) {
      finalReplyReceipts.delete(id);
    }
  }
  const receipt = finalReplyReceipts.get(executionId);
  if (receipt && receipt.normalizedText === normalizedText) {
    if (owner && receipt.delivery === "relayed") {
      finalReplyReceipts.set(executionId, {
        normalizedText,
        receivedAt: now,
        status: "pending",
        delivery: "owned",
        duplicateWhilePending: false,
        text,
        origin,
      });
      return true;
    }
    receipt.receivedAt = now;
    if (receipt.status === "pending") receipt.duplicateWhilePending = true;
    return false;
  }
  finalReplyReceipts.set(executionId, {
    normalizedText,
    receivedAt: now,
    status: "pending",
    delivery: owner ? "owned" : "relayed",
    duplicateWhilePending: false,
    text,
    origin,
  });
  return true;
}

function settleFinalReply(
  executionId: string,
  text: string,
  succeeded: boolean,
): void {
  const receipt = finalReplyReceipts.get(executionId);
  if (!receipt || receipt.normalizedText !== normalizedSpeechText(text)) {
    return;
  }
  if (succeeded) {
    useTtsPlaybackStore.getState().markReplyCompleted(executionId, text);
    receipt.status = "succeeded";
    receipt.duplicateWhilePending = false;
    return;
  }
  finalReplyReceipts.delete(executionId);
  if (!receipt.duplicateWhilePending) return;
  queueMicrotask(() => {
    speakFinalReplyNow(executionId, receipt.text, receipt.origin);
  });
}

function recordFinalReplyOwnerAcceptance(
  executionId: string,
  text: string,
): void {
  const normalizedText = normalizedSpeechText(text);
  if (!normalizedText) return;
  const receipt = finalReplyReceipts.get(executionId);
  if (receipt?.normalizedText === normalizedText) {
    receipt.delivery = "owned";
    receipt.receivedAt = Date.now();
    receipt.duplicateWhilePending = false;
    return;
  }
  finalReplyReceipts.set(executionId, {
    normalizedText,
    receivedAt: Date.now(),
    status: "pending",
    delivery: "owned",
    duplicateWhilePending: false,
    text,
  });
}

function cursorTextForSpeech(
  cursorKey: string,
  agentName?: string,
): string | null {
  const cursors = useCanvasStore.getState().agentCursors;
  let cursor = cursors.get(cursorKey);
  if (!cursor && agentName) {
    const normalized = agentName.split(" · ")[0]!.toLowerCase();
    for (const candidate of cursors.values()) {
      if (candidate.origin === "heartbeat") continue;
      const candidateName = candidate.name.split(" · ")[0]!.toLowerCase();
      if (candidateName !== normalized) continue;
      if (!cursor || candidate.lastSeen > cursor.lastSeen) cursor = candidate;
    }
  }
  if (!cursor) return null;

  const content = cursor.content?.trim();
  const full = cursor.contentFull?.trim();
  const text = full || content;
  return text ? normalizeAssistantText(text) : null;
}

function trySpeakFromMessages(
  executionId: string,
  origin?: SessionOrigin,
): boolean {
  const messages = useConversationStore.getState().messages;
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i]!;
    if (
      m.role === "assistant" &&
      m.executionId === executionId &&
      !m.isStreaming
    ) {
      return speakFinalReplyNow(executionId, m.content, origin);
    }
  }
  return false;
}

let conversationUnsub: (() => void) | null = null;
function attachConversationSub() {
  if (conversationUnsub) return;
  conversationUnsub = useConversationStore.subscribe((state, prev) => {
    if (state.messages === prev.messages) return;
    for (const [executionId, pending] of pendingSpeaks) {
      if (trySpeakFromMessages(executionId, pending.origin)) {
        pendingSpeaks.delete(executionId);
      }
    }
  });
}

async function performSpeakNow(
  text: string,
  sentenceCount?: number | null,
  options?: {
    preparedId?: string;
    executionId?: string;
    preserveActive?: boolean;
    ttsSource?: string;
    liveTalkOnly?: boolean;
    dropIfBusy?: boolean;
  },
): Promise<boolean> {
  // The TTS sidecar is Tauri-only; on web this would log
  // `invoke is undefined` errors on every assistant reply.
  if (!isTauri()) return false;
  if (options && isSpeechMutedForUserTurn(options)) {
    emitInterruptedPresentationSpeech(text, options);
    return true;
  }
  if (quietForTurn(options)) return true;
  if (
    options?.executionId &&
    !options.ttsSource?.startsWith("presentation:") &&
    !isLiveTalkSpeechAllowed(options.executionId)
  ) {
    return false;
  }
  const settings = useTtsSettingsStore.getState();
  if (!canSpeak(settings)) return false;
  const speechText = text.replace(/\s+/g, " ").trim();
  if (!speechText) return false;

  // Non-owner windows relay the request to the owner via a Tauri event.
  // The owner is the sole caller of the TTS sidecar — this preserves the
  // "one sidecar caller" invariant while enabling TTS from any monitor.
  if (getWindowRoleSync() !== "owner") {
    logTtsPipeline(
      `relay_to_owner execution_id=${options?.executionId ?? "-"} tts_source=${options?.ttsSource ?? "-"} text_len=${speechText.length}`,
    );
    void emit("denker://tts-speak", {
      text: speechText,
      sentenceCount,
      executionId: options?.executionId,
      ttsSource: options?.ttsSource,
      dropIfBusy: options?.dropIfBusy,
    });
    return true;
  }

  // Owner path: apply sentence slicing, track, and invoke the sidecar.
  const count =
    sentenceCount === undefined ? 1 : (sentenceCount ?? settings.sentenceCount);
  const utteranceText =
    sentenceCount === null ? speechText : firstSentences(speechText, count);
  if (!utteranceText) return false;

  // Same-text dedup. When the cursor crosses monitors mid-onboarding the
  // re-activated overlay can re-issue a setBubble with the same copy that
  // is currently playing — without dedup the sidecar would interrupt the
  // in-flight utterance and start over. 30s window matches the longest
  // typical bubble length so we don't suppress a legitimate replay later.
  const now = Date.now();
  const last = lastSpoken;
  if (
    last &&
    last.text === utteranceText &&
    last.ttsSource === options?.ttsSource &&
    now - last.at < SPEAK_DEDUP_MS
  ) {
    return false;
  }
  lastSpoken = {
    text: utteranceText,
    ttsSource: options?.ttsSource,
    at: now,
  };
  const provider = "denker";
  const utteranceId = `${now}-${Math.random().toString(36).slice(2)}`;
  if (activeNarration && !options?.preserveActive) {
    emitNarrationState({
      ...activeNarration,
      state: "cancelled",
      at: now,
    });
  }
  activeNarration = {
    utteranceId,
    text: utteranceText,
    provider,
    ttsSource: options?.ttsSource,
    executionId: options?.executionId,
    liveTalkOnly: options?.liveTalkOnly,
  };
  rememberNarrationForEcho(activeNarration, "started");

  track("desktop_voice_tts_spoken", {
    text_length: utteranceText.length,
    voice_id: settings.denkerVoiceId,
    sentence_count: count,
  });
  if (options?.executionId) {
    traceCheckpoint(options.executionId, "tts_sidecar_invoked", true);
  }
  logTtsPipeline(
    `native_invoke execution_id=${options?.executionId ?? "-"} tts_source=${options?.ttsSource ?? "-"} text_len=${utteranceText.length} utterance_id=${utteranceId}`,
  );

  try {
    // Only blocking Kokoro commands return terminal state. System voices
    // return as soon as queued and must settle through their native event.
    const engine = currentVoiceEngine();
    const narrationBudget = options?.ttsSource?.startsWith("presentation:")
      ? Math.max(120_000, 37_000 + utteranceText.split(/\s+/).length * 600)
      : 120_000;
    const terminalState = waitForUtteranceTerminal(
      utteranceId,
      narrationBudget,
    );
    const nativeCompletion = speakWithDenkerAgentVoice(
      utteranceText,
      settings.denkerVoiceId,
      {
        utteranceId,
        preparedId: options?.preparedId,
        streaming:
          options?.preserveActive === true ||
          options?.ttsSource?.startsWith("presentation:") === true ||
          options?.ttsSource?.startsWith("awaiting-input:") === true,
        executionId: options?.executionId,
        ttsSource: options?.ttsSource,
      },
    ).then((nativeTerminal) => {
      if (engine !== "system" && nativeTerminal) {
        settleUtteranceWaiter(utteranceId, nativeTerminal);
      }
      return terminalState;
    });
    // Modern Kokoro commands return the exact playback outcome. Older native
    // builds and system voices return void, so retain their identity until the
    // matching terminal event (or the bounded error timeout) settles it.
    // Native events and the watchdog must release the queue even when the
    // command never returns. Late command results cannot settle a newer turn.
    const terminal = await Promise.race([terminalState, nativeCompletion]);
    if (terminal === "error") {
      forgetFailedSpeak(utteranceText, options?.ttsSource);
      if (activeNarration?.utteranceId === utteranceId) {
        void cancelDenkerAgentVoice("explicit").catch(() => {});
      }
    }
    applyVoiceState({ utteranceId, state: terminal });
    if (terminal !== "finished") return false;
    if (
      options?.executionId &&
      options.ttsSource?.startsWith("presentation:")
    ) {
      markReplySpoken(options.executionId, utteranceText, "narration");
      presentationSpeechTurns.complete(
        options.executionId,
        !options.ttsSource.endsWith(":progress"),
      );
    }
    return true;
  } catch (e) {
    dropUtteranceWaiter(utteranceId);
    forgetFailedSpeak(utteranceText, options?.ttsSource);
    console.error("[tts] denker voice failed:", e);
    if (activeNarration?.utteranceId !== utteranceId) return false;
    applyVoiceState({ utteranceId, state: "error" });
    return false;
  }
}

function enqueueStandaloneSpeech(
  text: string,
  sentenceCount?: number | null,
  options?: {
    executionId?: string;
    ttsSource?: string;
    liveTalkOnly?: boolean;
    dropIfBusy?: boolean;
  },
): boolean {
  if (options && isSpeechMutedForUserTurn(options)) {
    emitInterruptedPresentationSpeech(text, options);
    return true;
  }
  if (quietForTurn(options)) return true;
  if (options?.ttsSource?.startsWith("presentation:")) {
    presentationQueue.enqueue(
      options.executionId,
      options.ttsSource,
      (isCurrent) =>
        enqueueNativeSpeech(
          text,
          sentenceCount,
          {
            ...options,
            preparation: takePresentationPreparation(options.ttsSource!),
          },
          isCurrent,
        ),
      () => replayPresentationNarration(options.ttsSource!),
    );
    return true;
  }
  const token = standaloneSpeechQueue.token + 1;
  standaloneSpeechQueue.token = token;
  standaloneSpeechQueue.source = options?.ttsSource;
  if (options?.ttsSource === "acknowledgement" && options.executionId) {
    queuedAcknowledgement = {
      executionId: options.executionId,
      token,
      liveTalkOnly: options.liveTalkOnly === true,
    };
  }
  standaloneSpeechQueue.queue = standaloneSpeechQueue.queue
    .catch(() => {})
    .then(async () => {
      if (standaloneSpeechQueue.token !== token) return;
      await enqueueNativeSpeech(text, sentenceCount, options ?? {}, () => {
        if (standaloneSpeechQueue.token !== token) return false;
        if (options?.ttsSource !== "acknowledgement") return true;
        if (
          !options.executionId ||
          queuedAcknowledgement?.executionId !== options.executionId ||
          queuedAcknowledgement.token !== token
        ) {
          return false;
        }
        if (queuedAcknowledgement.liveTalkOnly && !isContinuousVoiceActive()) {
          queuedAcknowledgement = null;
          return false;
        }
        queuedAcknowledgement = null;
        return true;
      });
    });
  trackSpeechWork(standaloneSpeechQueue.queue, options?.executionId);
  return true;
}

function enqueueNativeSpeech(
  text: string,
  sentenceCount: number | null | undefined,
  options: {
    preparation?: SpeechTicket;
    executionId?: string;
    preserveActive?: boolean;
    ttsSource?: string;
    liveTalkOnly?: boolean;
    dropIfBusy?: boolean;
  },
  shouldRun: () => boolean,
): Promise<boolean> {
  const queuedAt = Date.now();
  logTtsPipeline(
    `native_queue_enqueued execution_id=${options.executionId ?? "-"} tts_source=${options.ttsSource ?? "-"} text_len=${text.length}`,
  );
  const result = nativeSpeechQueue
    .catch(() => {})
    .then(async () => {
      await nativeCancellation;
      const preparedId = options.preparation
        ? await speechLookahead.take(options.preparation)
        : undefined;
      if (!shouldRun()) {
        logTtsPipeline(
          `native_queue_dropped execution_id=${options.executionId ?? "-"} tts_source=${options.ttsSource ?? "-"} queue_wait_ms=${Date.now() - queuedAt}`,
        );
        if (preparedId)
          void invoke("denker_agent_voice_discard_prepared", {
            preparedId,
          }).catch(() => {});
        return false;
      }
      logTtsPipeline(
        `native_queue_started execution_id=${options.executionId ?? "-"} tts_source=${options.ttsSource ?? "-"} queue_wait_ms=${Date.now() - queuedAt} text_len=${text.length}`,
      );
      try {
        return await performSpeakNow(text, sentenceCount, {
          ...options,
          preparedId,
        });
      } finally {
        speechLookahead.playback(false);
        if (preparedId)
          void invoke("denker_agent_voice_discard_prepared", {
            preparedId,
          }).catch(() => {});
      }
    });
  trackSpeechWork(result, options.executionId);
  nativeSpeechQueue = result.then(
    () => undefined,
    () => undefined,
  );
  return result;
}

function cancelStandaloneSpeechQueue(keepQuestion = false): void {
  standaloneSpeechQueue.cancellationToken += 1;
  queuedAcknowledgement = null;
  // The user has to hear a question to answer it.
  if (
    keepQuestion &&
    standaloneSpeechQueue.source?.startsWith("awaiting-input:")
  )
    return;
  standaloneSpeechQueue.token += 1;
}

function cancelReplySpeechQueue(executionId: string): void {
  const state = replySpeechQueues.get(executionId);
  if (!state) return;
  state.token += 1;
  speechLookahead.prune();
  state.queue = Promise.resolve();
}

function clearReplySpeechQueues(): void {
  for (const state of replySpeechQueues.values()) state.token += 1;
  replySpeechQueues.clear();
  speechLookahead.reset();
}

function enqueueReplySpeech(
  executionId: string,
  text: string,
  options?: {
    ttsSource?: string;
    fallbackText?: string;
    onlyAfterPriorFailure?: boolean;
    finalReplyText?: string;
  },
): boolean {
  // Agent replies arrive as markdown — strip formatting markers (asterisks,
  // backticks, headers, link syntax) so the voice doesn't read them aloud.
  const speechText = plainSpeechText(text);
  const groups = speechText ? [speechText] : [];
  const fallbackText = options?.fallbackText
    ? plainSpeechText(options.fallbackText)
    : undefined;
  return enqueueReplySpeechGroups(executionId, groups, {
    ...options,
    fallbackText,
  });
}

function enqueueReplySpeechGroups(
  executionId: string,
  groups: string[],
  options?: {
    ttsSource?: string;
    fallbackText?: string;
    onlyAfterPriorFailure?: boolean;
    finalReplyText?: string;
  },
): boolean {
  if (!isLiveTalkSpeechAllowed(executionId)) return false;
  if (groups.length === 0) return false;
  cancelPendingAcknowledgement(executionId);
  logTtsPipeline(
    `reply_groups_enqueued execution_id=${executionId} tts_source=${options?.ttsSource ?? "-"} group_count=${groups.length} text_len=${groups.join(" ").length}`,
  );

  if (getWindowRoleSync() !== "owner") {
    if (options?.onlyAfterPriorFailure) return true;
    markReplySpoken(executionId, groups.join(" "));
    void emit("denker://tts-speak-sequence", {
      groups,
      executionId,
      ttsSource: options?.ttsSource,
    });
    return true;
  }

  if (options?.ttsSource !== "acknowledgement") {
    preemptActiveAcknowledgement();
  }

  let state = replySpeechQueues.get(executionId);
  if (!state) {
    state = {
      token: (replySpeechQueueToken += 1),
      queue: Promise.resolve(),
      lastSpeechSucceeded: true,
    };
    replySpeechQueues.set(executionId, state);
  }
  const token = state.token;
  const preparations = options?.onlyAfterPriorFailure
    ? []
    : groups.map((group) =>
        prepareQueuedSpeech(group, () => {
          const latest = replySpeechQueues.get(executionId);
          return Boolean(
            latest &&
            latest.token === token &&
            isLiveTalkSpeechAllowed(executionId) &&
            !isSpeechMutedForUserTurn({ executionId }),
          );
        }),
      );
  const nextQueue = state.queue.then(async () => {
    for (const [index, group] of groups.entries()) {
      const current = replySpeechQueues.get(executionId);
      if (!current || current.token !== token) return;
      if (options?.onlyAfterPriorFailure && current.lastSpeechSucceeded) {
        continue;
      }
      const speechGroup =
        !current.lastSpeechSucceeded && options?.fallbackText
          ? options.fallbackText
          : group;
      const succeeded = await enqueueNativeSpeech(
        speechGroup,
        null,
        {
          executionId,
          preserveActive: true,
          preparation: preparations[index],
          ttsSource: options?.ttsSource,
        },
        () => {
          const latest = replySpeechQueues.get(executionId);
          return Boolean(
            latest &&
            latest.token === token &&
            isLiveTalkSpeechAllowed(executionId),
          );
        },
      );
      const latest = replySpeechQueues.get(executionId);
      if (!latest || latest !== current || latest.token !== token) return;
      latest.lastSpeechSucceeded = succeeded;
      if (succeeded) markReplySpoken(executionId, speechGroup);
    }
    const current = replySpeechQueues.get(executionId);
    if (current?.token === token && options?.finalReplyText) {
      settleFinalReply(
        executionId,
        options.finalReplyText,
        current.lastSpeechSucceeded,
      );
    }
    if (
      current?.token === token &&
      current.queue === nextQueue &&
      current.lastSpeechSucceeded
    ) {
      replySpeechQueues.delete(executionId);
      speechLookahead.prune();
    }
  });
  state.queue = trackSpeechWork(nextQueue, executionId);
  return true;
}

function flushStreamingTail(
  executionId: string,
  finalText: string,
  options?: {
    preserveQueuedPrefix?: boolean;
    finalReplyText?: string;
  },
): "unmatched" | "queued" | "deduped" {
  const state = streamingSpeaks.get(executionId);
  if (!state) return "unmatched";
  clearStreamingTrailingTimer(executionId);
  const spokenPrefix = state.text.slice(0, state.spokenIndex).trim();
  if (
    options?.preserveQueuedPrefix &&
    !finalTextStartsWithPrefix(finalText, spokenPrefix)
  ) {
    return "unmatched";
  }
  const remaining = unspokenReplySentences(
    executionId,
    remainingTextAfterPrefix(finalText, spokenPrefix),
  );
  streamingSpeaks.delete(executionId);
  if (!remaining && !state.openingGroupQueued) {
    logTtsPipeline(
      `final_reply_deduped execution_id=${executionId} reason=already_spoken`,
    );
    if (options?.finalReplyText)
      settleFinalReply(executionId, options.finalReplyText, true);
    return "deduped";
  }
  // With nothing left, a recovery-only copy waits behind the opening: skipped
  // after successful playback, it speaks the whole final if the opening failed.
  const queued = enqueueReplySpeech(executionId, remaining || finalText, {
    ttsSource: options?.preserveQueuedPrefix
      ? "final_message_tail"
      : "cursor_fallback_tail",
    fallbackText: finalText,
    onlyAfterPriorFailure: !remaining,
    finalReplyText: options?.finalReplyText,
  });
  if (!queued && options?.finalReplyText) {
    settleFinalReply(executionId, options.finalReplyText, false);
  }
  return "queued";
}

function clearStreamingTrailingTimer(executionId: string): void {
  const timer = streamingTrailingTimers.get(executionId);
  if (timer !== undefined) {
    clearTimeout(timer);
    streamingTrailingTimers.delete(executionId);
  }
}

// Cap a streamed flush to speakable groups (<=2 sentences/200ch): a single
// huge utterance delays echo matching, cancellation and the sidecar's first
// chunk.
function enqueueStreamingGroup(
  executionId: string,
  group: string,
  spokenPrefix: string,
): void {
  const unspoken = unspokenReplySentences(executionId, group);
  if (!unspoken) return;
  const groups = speechGroups(unspoken);
  enqueueReplySpeechGroups(executionId, groups.length ? groups : [unspoken], {
    fallbackText: plainSpeechText(spokenPrefix),
    ttsSource: "streaming_chunk",
  });
}

function scheduleStreamingTrailingFlush(
  executionId: string,
  snapshotText: string,
  origin?: SessionOrigin,
): void {
  // Overwritten below rather than deleted first: the entry counts as busy.
  clearTimeout(streamingTrailingTimers.get(executionId));
  const state = streamingSpeaks.get(executionId);
  if (!state || !heldTrailingSentence(state.text.slice(state.spokenIndex)))
    return void streamingTrailingTimers.delete(executionId);
  logTtsPipeline(
    `stream_reply_trailing_scheduled execution_id=${executionId} text_len=${snapshotText.length}`,
  );
  streamingTrailingTimers.set(
    executionId,
    setTimeout(() => {
      streamingTrailingTimers.delete(executionId);
      if (origin === "heartbeat" || finalReplyReceipts.has(executionId)) return;
      if (streamingSpeechHeld(executionId)) return;
      if (!isLiveTalkSpeechAllowed(executionId)) return;
      const current = streamingSpeaks.get(executionId);
      if (!current || current.text !== snapshotText) return;
      const group = forceAdvanceStreamingReply(current);
      if (!group) return;
      logTtsPipeline(
        `stream_reply_trailing_flushed execution_id=${executionId} text_len=${group.length}`,
      );
      enqueueStreamingGroup(
        executionId,
        group,
        current.text.slice(0, current.spokenIndex),
      );
    }, STREAMING_TRAILING_FLUSH_MS),
  );
}

function speakFinalReplyNow(
  executionId: string,
  text: string,
  origin?: SessionOrigin,
): boolean {
  if (origin === "heartbeat") return false;
  if (!isLiveTalkSpeechAllowed(executionId)) return false;
  const queuedPresentation = presentationQueue.wait(executionId);
  if (queuedPresentation || isPresentationNarrating(executionId)) {
    const token = standaloneSpeechQueue.cancellationToken;
    const pending = queuedPresentation ?? nativeSpeechQueue;
    void pending.then(() => {
      if (standaloneSpeechQueue.cancellationToken === token)
        speakFinalReplyNow(executionId, text, origin);
    });
    return true;
  }
  cancelPendingAcknowledgement(executionId);
  // Not collapsed here: a list marker on its own line is stripped as one.
  const trimmed = normalizeAssistantText(text);
  if (!trimmed) return false;
  const narratedMatch = replyTailAfterSpokenHistory(executionId, trimmed);
  // Queued/active presentation speech is awaited above. Elapsed time cannot
  // establish whether a final should be spoken; use explicit speech ownership.
  const owner = getWindowRoleSync() === "owner";
  if (!acceptFinalReply(executionId, trimmed, owner, origin)) {
    track("desktop_voice_tts_reply_deduped", { execution_id: executionId });
    logTtsPipeline(
      `final_reply_deduped execution_id=${executionId} text_len=${trimmed.length}`,
    );
    return false;
  }
  // Another app plays sound or has the microphone: shown, not read aloud (`tts-quiet-turns.ts`).
  if (isQuietTurn(executionId)) {
    streamingSpeaks.delete(executionId);
    settleFinalReply(executionId, trimmed, true);
    cueQuietTurn(executionId);
    logTtsPipeline(
      `final_reply_suppressed execution_id=${executionId} reason=quiet`,
    );
    return false;
  }
  if (!owner) {
    void emit("denker://tts-final-reply", { executionId, text: trimmed });
    return true;
  }
  void emit("denker://tts-final-reply-accepted", {
    executionId,
    text: trimmed,
    ownerLabel: getMyWindowLabel(),
  });
  traceCheckpoint(executionId, "tts_final_message_received", true);
  logTtsPipeline(
    `final_reply_received execution_id=${executionId} text_len=${trimmed.length}`,
  );
  cancelStandaloneSpeechQueue(true);
  const historyMatch = narratedMatch;
  // A narrated guide step, bubble or ack already read this final aloud.
  // Re-speaking it back-to-back feels duplicated and slow.
  if (wasStandaloneSpoken(trimmed)) {
    track("desktop_voice_tts_reply_deduped", { execution_id: executionId });
    logTtsPipeline(
      `final_reply_deduped execution_id=${executionId} reason=standalone_spoken text_len=${trimmed.length}`,
    );
    settleFinalReply(executionId, trimmed, true);
    return false;
  }
  // Narration silences only what it said; a stopped presentation, the turn.
  if (presentationSpeechTurns.outcome(executionId) === "stopped") {
    streamingSpeaks.delete(executionId);
    settleFinalReply(executionId, trimmed, true);
    logTtsPipeline(
      `final_reply_suppressed execution_id=${executionId} reason=stopped`,
    );
    track("desktop_voice_tts_final_silenced_by_stop", {
      execution_id: executionId,
    });
    return false;
  }
  if (historyMatch?.type === "duplicate") {
    track("desktop_voice_tts_reply_deduped", { execution_id: executionId });
    settleFinalReply(executionId, trimmed, true);
    return false;
  }
  const tail = flushStreamingTail(executionId, trimmed, {
    preserveQueuedPrefix: true,
    finalReplyText: trimmed,
  });
  if (tail === "deduped") return false;
  if (tail === "queued") {
    track("desktop_voice_tts_enqueued", {
      execution_id: executionId,
      source: "final_message_tail",
    });
    return true;
  }
  const streamingState = streamingSpeaks.get(executionId);
  const streamedSpeechQueued =
    streamingState !== undefined && hasQueuedStreamingSpeech(streamingState);
  const textToSpeak = unspokenReplySentences(
    executionId,
    historyMatch?.type === "tail" ? historyMatch.text : trimmed,
  );
  if (!textToSpeak) {
    logTtsPipeline(
      `final_reply_deduped execution_id=${executionId} reason=narration_overlap`,
    );
    settleFinalReply(executionId, trimmed, true);
    return false;
  }
  // An unmatched final after streamed commentary keeps that opening queued.
  const keepOpening = !historyMatch && streamedSpeechQueued;
  if (!keepOpening) {
    cancelReplySpeechQueue(executionId);
    preemptActiveReplyForExecution(executionId);
  }
  streamingSpeaks.delete(executionId);
  const spoken = enqueueReplySpeech(executionId, textToSpeak, {
    ttsSource:
      streamedSpeechQueued && !keepOpening
        ? "final_message_tail"
        : "final_message",
    finalReplyText: trimmed,
  });
  if (spoken) {
    track("desktop_voice_tts_enqueued", {
      execution_id: executionId,
      source: "final_message",
    });
  } else {
    settleFinalReply(executionId, trimmed, false);
  }
  return spoken;
}

/**
 * Test-only helper: clears module-level state that would otherwise leak
 * between vitest runs in the same module (pendingSpeaks map and the
 * conversation-store subscription).
 */
export function _resetTtsForTests() {
  pendingSpeaks.clear();
  resetSpeechStopKeyForTests();
  resetTtsAcknowledgementSchedulerForTests();
  resetLiveTalkSpeechOwnershipForTests();
  resetUserSpeechInterruptionsForTests();
  clearReplyHistories();
  presentationSpeechTurns.reset();
  presentationQueue.reset();
  finalReplyReceipts.clear();
  streamingSpeaks.clear();
  for (const executionId of streamingTrailingTimers.keys()) {
    clearStreamingTrailingTimer(executionId);
  }
  clearReplySpeechQueues();
  replySpeechQueueToken = 0;
  cancelStandaloneSpeechQueue();
  standaloneSpeechQueue.queue = Promise.resolve();
  nativeSpeechQueue = Promise.resolve();
  nativeCancellation = Promise.resolve();
  resetUtteranceWaiters();
  lastSpoken = null;
  activeNarration = null;
  resetTtsEchoMemoryForTests();
  queuedAcknowledgement = null;
  if (conversationUnsub) {
    conversationUnsub();
    conversationUnsub = null;
  }
}

export function _setTtsAcknowledgementsEnabledForTests(enabled: boolean) {
  setDeterministicAcknowledgementsEnabledForTests(enabled);
}

export const useTTS = create<State & Actions>((_set, get) => ({
  speakingState: null,

  ...createTtsAcknowledgementActions({
    onReady: (executionId, text, kind) => {
      track("desktop_voice_tts_enqueued", {
        execution_id: executionId,
        source: "acknowledgement",
      });
      enqueueStandaloneSpeech(text, null, {
        executionId,
        ttsSource: "acknowledgement",
        liveTalkOnly: kind === "live_talk",
      });
    },
    onScheduled: () => {
      cancelStandaloneSpeechQueue();
      clearReplySpeechQueues();
    },
    onCancelled: cancelQueuedOrActiveAcknowledgement,
    onKindCancelled: cancelLiveTalkAcknowledgements,
    isLiveTalkEligible: isContinuousVoiceActive,
  }),

  isActive: () => get().speakingState === "started",

  cancel: (reason = "explicit") => {
    if (!isTauri()) return;
    if (reason === "stop_key") {
      // The stop key keeps the runs it silences quiet until they end.
      if (activeNarration) muteSpeechForUserTurn(activeNarration);
      muteQueuedSpeech();
      pendingSpeaks.clear();
    }
    cancelPendingAcknowledgements();
    cancelStandaloneSpeechQueue();
    clearReplySpeechQueues();
    for (const executionId of [...streamingTrailingTimers.keys()]) {
      clearStreamingTrailingTimer(executionId);
    }
    logTtsPipeline(
      `cancel_requested reason=${reason} active_utterance_id=${activeNarration?.utteranceId ?? "-"} execution_id=${activeNarration?.executionId ?? "-"} tts_source=${activeNarration?.ttsSource ?? "-"} hard_cancel=${isHardCancel(reason)}`,
    );
    // A held reply that is not resumed has to stop, whatever the reason.
    if (!isHardCancel(reason) && !isLiveTalkSpeechHeld()) return;
    if (
      reason === "voice_barge_in" &&
      activeNarration?.ttsSource?.startsWith("presentation:")
    ) {
      logTtsPipeline(
        `narration_preserved reason=${reason} utterance_id=${activeNarration.utteranceId} execution_id=${activeNarration.executionId ?? "-"} tts_source=${activeNarration.ttsSource}`,
      );
      return false;
    }
    clearLiveTalkSpeechHold(reason);
    dropSpeechWork();
    if (reason === "explicit" || reason === "stop_key") {
      presentationQueue.cancel();
      window.dispatchEvent(new Event("denker:presentation-stop"));
    }
    clearReplyHistories();
    streamingSpeaks.clear();
    // The terminal `cancelled` event can no longer be matched once activeNarration
    // is nulled below, so reset speakingState here or isActive() sticks true and
    // swallows the voice-toggle chord forever.
    if (get().speakingState === "started") {
      _set({ speakingState: "cancelled" });
    }
    const cancelled = activeNarration;
    if (reason === "user_speech" && cancelled) {
      muteSpeechForUserTurn(cancelled);
      settleUtteranceWaiter(cancelled.utteranceId, "cancelled");
    }
    if (cancelled) rememberNarrationForEcho(cancelled, "cancelled");
    activeNarration = null;
    if (cancelled) {
      emitNarrationState({
        ...cancelled,
        state: "cancelled",
        speechInterruptedByUser: reason === "user_speech",
        at: Date.now(),
      });
    }
    if (getWindowRoleSync() !== "owner") {
      void emit("denker://tts-cancel", { reason });
      return;
    }
    if (reason === "stop_key") announceSpeechStopped();
    track("desktop_voice_tts_cancelled", { reason });
    lastSpoken = null;
    nativeCancellation = cancelDenkerAgentVoice(reason);
  },

  cancelPresentationSpeech: (ttsSource) => {
    presentationQueue.cancelSource(ttsSource);
    // A standalone item that has not started yet: moving the token on is
    // what makes its turn in the queue a no-op.
    if (standaloneSpeechQueue.source === ttsSource) {
      standaloneSpeechQueue.token += 1;
      standaloneSpeechQueue.source = undefined;
    }
    logTtsPipeline(
      `presentation_source_cancelled tts_source=${ttsSource} active_utterance_id=${activeNarration?.ttsSource === ttsSource ? activeNarration.utteranceId : "-"}`,
    );
    if (getWindowRoleSync() !== "owner") {
      void emit("denker://tts-cancel-presentation", { ttsSource });
      return;
    }
    releasePresentationPreparation(ttsSource);
    // Cut off, so not heard: the same line may be asked for again at once.
    if (lastSpoken?.ttsSource === ttsSource) lastSpoken = null;
    if (activeNarration?.ttsSource !== ttsSource) return;
    activeNarration.presentationAttemptCancelled = true;
    const utteranceId = activeNarration.utteranceId;
    nativeCancellation = cancelDenkerAgentVoice("explicit");
    void nativeCancellation.then(() =>
      applyVoiceState({ utteranceId, state: "cancelled" }),
    );
  },

  hasPendingPresentationSpeech: (executionId) =>
    presentationQueue.hasPending(executionId ?? ""),

  speak: (text, sentenceCount, options) => {
    if (
      options?.dropIfBusy &&
      options.ttsSource &&
      getWindowRoleSync() === "owner" &&
      (get().hasPendingPresentationSpeech(options.executionId) ||
        isPresentationNarrating(options.executionId))
    ) {
      releasePresentationPreparation(options.ttsSource);
      logTtsPipeline(
        `presentation_dropped_busy execution_id=${options.executionId ?? "-"} tts_source=${options.ttsSource}`,
      );
      return;
    }
    enqueueStandaloneSpeech(text, sentenceCount, options);
  },

  speakStreamingReply: (executionId, cumulativeText, origin, streamId) => {
    if (origin === "heartbeat" || finalReplyReceipts.has(executionId)) return;
    if (isQuietTurn(executionId)) return;
    if (streamingSpeechHeld(executionId)) return;
    if (!isLiveTalkSpeechAllowed(executionId)) return;
    const text =
      normalizeAssistantText(cumulativeText) +
      (/\s$/.test(cumulativeText) ? " " : "");
    if (!text) return;
    cancelPendingAcknowledgement(executionId);
    traceCheckpoint(executionId, "tts_reply_requested", true);
    logTtsPipelineStreamStart(
      "stream_reply_received",
      executionId,
      text.length,
    );

    let state = streamingSpeaks.get(executionId);
    if (!state) {
      state = createStreamingSpeak();
      streamingSpeaks.set(executionId, state);
    }
    const turn = switchStreamingMessage(state, text, streamId);
    if (turn.kind === "retired") return;
    if (turn.kind === "reset") {
      // The old message's settle timer is resolved here: its held sentence
      // was just flushed, so it must not fire again.
      clearStreamingTrailingTimer(executionId);
      logTtsPipeline(
        `stream_reply_reset execution_id=${executionId} from_stream=${turn.retiredStreamId} to_stream=${streamId} flushed_len=${turn.flushed?.text.length ?? 0}`,
      );
      if (turn.flushed) {
        enqueueStreamingGroup(
          executionId,
          turn.flushed.text,
          turn.flushed.spokenPrefix,
        );
      }
    }
    const group = advanceStreamingReply(state, text);
    if (!group) {
      // No stable sentence yet — but a finished single sentence held only
      // by the trailing-period guard (commentary before tool calls) still
      // speaks after a short settle delay instead of staying silent.
      scheduleStreamingTrailingFlush(executionId, state.text, origin);
      return;
    }
    clearStreamingTrailingTimer(executionId);
    enqueueStreamingGroup(
      executionId,
      group,
      state.text.slice(0, state.spokenIndex),
    );
    // The remainder may end in a held trailing period with no more chunks
    // coming — schedule its settle flush as well.
    scheduleStreamingTrailingFlush(executionId, state.text, origin);
  },

  clearReplyDedup: (executionId) => {
    presentationSpeechTurns.clear(executionId);
    forgetReplyHistory(executionId);
    finalReplyReceipts.delete(executionId);
    pendingSpeaks.delete(executionId);
    streamingSpeaks.delete(executionId);
    clearStreamingTrailingTimer(executionId);
    cancelReplySpeechQueue(executionId);
  },

  resumeAnsweredTurn: (executionId) => {
    get().clearReplyDedup(executionId);
    unmuteSpeechForUserTurn({ executionId });
    resumeAnsweredLiveTalkExecution(executionId);
  },

  speakFinalReply: (executionId, text, origin) => {
    if (!isLiveTalkSpeechAllowed(executionId)) {
      logTtsPipeline(
        `final_reply_blocked execution_id=${executionId} reason=retired`,
      );
      return;
    }
    clearStreamingTrailingTimer(executionId);
    pendingSpeaks.delete(executionId);
    speakFinalReplyNow(executionId, text, origin);
  },

  speakLatestReply: (
    cursorKey,
    executionId,
    origin,
    agentName,
    allowUnprovenancedCursorFallback = false,
  ) => {
    // Heartbeat agents run silently in the background — do not speak.
    if (origin === "heartbeat") return;
    if (executionId && !isLiveTalkSpeechAllowed(executionId)) return;
    if (executionId) traceCheckpoint(executionId, "tts_reply_requested", true);
    const cursorSpeech = cursorTextForSpeech(cursorKey, agentName);
    const hasStreamingTail = executionId
      ? streamingSpeaks.has(executionId)
      : false;
    if (executionId) {
      if (
        (!hasStreamingTail &&
          cursorSpeech &&
          hasRecentReplySpeak(executionId, cursorSpeech)) ||
        pendingSpeaks.has(executionId)
      ) {
        track("desktop_voice_tts_reply_deduped", { execution_id: executionId });
        return;
      }
    }
    attachConversationSub();
    // Fall back to the authoritative assistant message scoped to this
    // execution. Chat-session-driven flows update conversation-store via
    // `message.created` and may never stream text through the cursor.
    if (executionId && trySpeakFromMessages(executionId, origin)) return;

    if (!executionId) {
      if (cursorSpeech) {
        get().speak(plainSpeechText(cursorSpeech), null);
      }
      return;
    }

    // Buffer briefly for the authoritative final assistant message. If it
    // does not arrive, fall back to cursor text so legacy/non-chat paths still
    // speak instead of going silent.
    pendingSpeaks.set(executionId, {
      cursorKey,
      executionId,
      origin,
      agentName,
      allowCursorFallback: hasStreamingTail || allowUnprovenancedCursorFallback,
      scheduledAt: Date.now(),
    });
    logTtsPipeline(
      `latest_reply_waiting_final execution_id=${executionId} cursor_text_len=${cursorSpeech?.length ?? 0}`,
    );
    setTimeout(() => {
      const entry = pendingSpeaks.get(executionId);
      if (!entry) return;
      if (trySpeakFromMessages(executionId, entry.origin)) {
        pendingSpeaks.delete(executionId);
        return;
      }
      pendingSpeaks.delete(executionId);
      if (presentationSpeechTurns.has(entry.executionId)) {
        streamingSpeaks.delete(entry.executionId);
        return;
      }
      if (!entry.allowCursorFallback) return;
      // Last-chance cursor fallback after timeout.
      const cursorSpeech = cursorTextForSpeech(
        entry.cursorKey,
        entry.agentName,
      );
      if (
        cursorSpeech &&
        !isAcknowledgementForExecution(cursorSpeech, entry.executionId)
      ) {
        const tail = flushStreamingTail(entry.executionId, cursorSpeech);
        if (tail === "deduped") return;
        if (tail === "queued") {
          track("desktop_voice_tts_enqueued", {
            execution_id: entry.executionId,
            source: "cursor_fallback",
          });
          return;
        }
        track("desktop_voice_tts_enqueued", {
          execution_id: entry.executionId,
          source: "cursor_fallback",
        });
        enqueueReplySpeech(entry.executionId, cursorSpeech, {
          ttsSource: "cursor_fallback",
        });
      }
    }, FINAL_MESSAGE_WAIT_MS);
  },
}));

// ---------------------------------------------------------------------
// Sidecar wiring — once-per-process listener attachment + initial
// voice-list request. Mirrors `use-dictation.ts` so the TTS module
// is self-bootstrapping; consumers just call `useTTS.getState().speak()`.
// ---------------------------------------------------------------------

function applyVoiceState(event: DenkerAgentVoiceStateEvent): void {
  logTtsPipeline(
    `playback_state state=${event.state} utterance_id=${event.utteranceId} matched=${event.utteranceId === activeNarration?.utteranceId} execution_id=${activeNarration?.executionId ?? "-"} tts_source=${activeNarration?.ttsSource ?? "-"}`,
  );
  settleUtteranceWaiter(event.utteranceId, event.state);
  const narration = activeNarration;
  if (!narration || narration.provider !== "denker") return;
  if (event.utteranceId !== narration.utteranceId) return;
  const state = event.state;
  speechLookahead.playback(
    state === "started" && currentVoiceEngine() === "kokoro",
  );
  useTTS.setState({
    speakingState: state === "error" ? null : state,
  });
  emitNarrationState({
    ...narration,
    state,
    at: Date.now(),
  });
  if (
    state === "finished" &&
    narration.executionId &&
    narration.ttsSource?.startsWith("presentation:")
  ) {
    markReplySpoken(narration.executionId, narration.text, "narration");
    presentationSpeechTurns.complete(
      narration.executionId,
      !narration.ttsSource.endsWith(":progress"),
    );
  }
  if (state !== "started") {
    rememberNarrationForEcho(narration, state === "error" ? "error" : state);
    activeNarration = null;
  }
}

let bootstrapped = false;

function bootstrapTtsSidecar() {
  if (bootstrapped) return;
  bootstrapped = true;

  // Playback state feeds the stop key (`tts-stop-key.ts`), which also mirrors
  // it to the other windows. The key stops speech only: it never starts
  // dictation, and on a mirror `cancel()` relays to the owner.
  useTTS.subscribe((state) => reportSpeechPlayback(state.speakingState));
  installSpeechStopKey(() => useTTS.getState().cancel("stop_key"));

  void listen<DenkerAgentVoiceStateEvent>(
    "denker-agent-voice-state",
    (event) => {
      applyVoiceState(event.payload);
    },
  );

  // Relay listeners — only the owner acts on these. Mirror windows emit
  // `denker://tts-speak` / `denker://tts-cancel` instead of invoking the
  // sidecar directly. The owner's speak() takes the sidecar path; no loop.
  void listen<{
    text: string;
    sentenceCount?: number | null;
    executionId?: string;
    ttsSource?: string;
    dropIfBusy?: boolean;
  }>("denker://tts-speak", (event) => {
    if (getWindowRoleSync() !== "owner") return;
    useTTS.getState().speak(event.payload.text, event.payload.sentenceCount, {
      executionId: event.payload.executionId,
      ttsSource: event.payload.ttsSource,
      dropIfBusy: event.payload.dropIfBusy,
    });
  });

  void listen<{
    groups: string[];
    executionId: string;
    ttsSource?: string;
  }>("denker://tts-speak-sequence", (event) => {
    if (getWindowRoleSync() !== "owner") return;
    const text = (event.payload.groups ?? []).join(" ").trim();
    if (!text) return;
    enqueueReplySpeech(event.payload.executionId, text, {
      ttsSource: event.payload.ttsSource,
    });
  });

  void listen<{
    executionId: string;
    text: string;
  }>("denker://tts-final-reply", (event) => {
    if (getWindowRoleSync() !== "owner") return;
    speakFinalReplyNow(event.payload.executionId, event.payload.text);
  });

  void listen<{
    executionId: string;
    text: string;
    ownerLabel: string;
  }>("denker://tts-final-reply-accepted", (event) => {
    if (
      getWindowRoleSync() === "owner" ||
      event.payload.ownerLabel === getMyWindowLabel()
    ) {
      return;
    }
    recordFinalReplyOwnerAcceptance(
      event.payload.executionId,
      event.payload.text,
    );
  });

  void listen<{ ttsSource: string }>(
    "denker://tts-cancel-presentation",
    (event) => {
      if (getWindowRoleSync() === "owner")
        useTTS.getState().cancelPresentationSpeech(event.payload.ttsSource);
    },
  );

  void listen<{ reason?: DenkerAgentVoiceCancelReason }>(
    "denker://tts-cancel",
    (event) => {
      if (getWindowRoleSync() !== "owner") return;
      useTTS.getState().cancel(event.payload?.reason);
    },
  );

  // No Apple/system TTS discovery here. Denker's packaged ONNX voices are the
  // only read-aloud provider.
}

if (typeof window !== "undefined" && isTauri()) {
  bootstrapTtsSidecar();
}
